import React, { useEffect, useState } from "react";
import { Nav, NavItem, NavLink } from "reactstrap";
import classnames from "classnames";
import PropTypes from "prop-types";
import {
    useFloating,
    useClick,
    useDismiss,
    useInteractions,
    FloatingPortal,
    offset,
    flip,
    shift,
} from "@floating-ui/react";
import stringUtils from "../../../_services/string-utils";
import listUtils from "../../../_services/list-utils";
import styled from "styled-components";
import { MdClose, MdDelete } from "react-icons/md";
import { iconButtonCss } from "../../StyledComponents/PathwayIcons";
import { PathwayTooltip } from "../PathwayTooltip";

function AddTabDropdown({ availableTabs, onAdd, setActiveTab }) {
    const [isOpen, setIsOpen] = useState(false);

    const { refs, floatingStyles, context } = useFloating({
        placement: "bottom-start",
        open: isOpen,
        onOpenChange: setIsOpen,
        middleware: [offset(4), flip(), shift({ padding: 8 })],
    });
    const click = useClick(context);
    const dismiss = useDismiss(context);
    const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss]);

    return (
        <>
            <a
                ref={refs.setReference}
                role="button"
                className="nav-link"
                style={{ cursor: "pointer" }}
                {...getReferenceProps()}
            >
                <strong>+</strong>
            </a>
            {isOpen && (
                <FloatingPortal>
                    <div
                        ref={refs.setFloating}
                        className="dropdown-menu show"
                        style={{ ...floatingStyles, zIndex: 9999 }}
                        {...getFloatingProps()}
                    >
                        {availableTabs.map((item, index) => (
                            <button
                                key={"language-setup-tab-dropdown-" + index}
                                className="dropdown-item"
                                style={{ cursor: "pointer" }}
                                onClick={() => {
                                    onAdd(item.identifier);
                                    setActiveTab(item.identifier);
                                    setIsOpen(false);
                                }}
                            >
                                <strong>{item.name}</strong>
                            </button>
                        ))}
                    </div>
                </FloatingPortal>
            )}
        </>
    );
}

const UncontrolledTabNavigation = ({
    defaultTab,
    currentTab,
    tabList,
    children,
    dataCy,
    removeTab,
    addNewTab,
    availableTabs,
}) => {
    const [activeTab, setActiveTab] = useState(defaultTab);

    useEffect(() => {
        if (currentTab) {
            setActiveTab(currentTab);
        }
    }, [currentTab, setActiveTab]);

    const childArray = React.Children.toArray(children);

    return (
        <>
            {tabList && (
                <>
                    <Nav tabs data-cy={dataCy || undefined}>
                        {tabList.map((item, index) => {
                            return (
                                <NavItem
                                    key={index}
                                    data-cy={
                                        stringUtils.formatUppercaseIdentifierToLowercase(item.identifier) + "-tab-link"
                                    }
                                >
                                    <NavLink
                                        data-cy={
                                            stringUtils.formatUppercaseIdentifierToLowercase(item.identifier) + "-tab"
                                        }
                                        className={activeTab === item.identifier ? "active" : undefined}
                                        onClick={() => setActiveTab(item.identifier)}
                                        disabled={activeTab === item.identifier}
                                    >
                                        {item.tooltip || item.onDelete ? (
                                            <TabLabel>
                                                {item.name}
                                                {item.tooltip && (
                                                    <PathwayTooltip
                                                        id={item.identifier + "-tooltip"}
                                                        iconSize="var(--iconSize-xs)"
                                                    >
                                                        {item.tooltip}
                                                    </PathwayTooltip>
                                                )}
                                                {item.onDelete && activeTab === item.identifier && (
                                                    <TabDeleteButton
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            item.onDelete();
                                                        }}
                                                        aria-label="Löschen"
                                                    >
                                                        <MdDelete />
                                                    </TabDeleteButton>
                                                )}
                                            </TabLabel>
                                        ) : (
                                            item.name
                                        )}
                                    </NavLink>
                                    {removeTab && item.removable && activeTab !== item.identifier && (
                                        <DismissButton>
                                            <MdClose onClick={() => removeTab(item.identifier)} />
                                        </DismissButton>
                                    )}
                                </NavItem>
                            );
                        })}

                        {addNewTab && !listUtils.isEmpty(availableTabs) && (
                            <NavItem>
                                <AddTabDropdown
                                    availableTabs={availableTabs}
                                    onAdd={addNewTab}
                                    setActiveTab={setActiveTab}
                                />
                            </NavItem>
                        )}
                    </Nav>
                    {tabList && (
                        <div className="tab-content">
                            {tabList.map((tab, index) => (
                                <div
                                    key={index}
                                    className={classnames("tab-pane", {
                                        active: activeTab === tab.identifier,
                                        show: activeTab === tab.identifier,
                                    })}
                                    data-cy={
                                        stringUtils.formatUppercaseIdentifierToLowercase(tab.identifier) +
                                        "-tab-content"
                                    }
                                >
                                    {childArray[index]}
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
        </>
    );
};

export default UncontrolledTabNavigation;

UncontrolledTabNavigation.propTypes = {
    defaultTab: PropTypes.string.isRequired,
    tabList: PropTypes.array.isRequired,
    dataCy: PropTypes.string.isRequired,
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]).isRequired,
};

const TabLabel = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
`;

const DismissButton = styled.div`
    width: 1rem;
    display: grid;

    & > svg:hover {
        color: var(--text-muted);
    }

    & > svg:active {
        color: var(--text-heading);
    }
`;

const TabDeleteButton = styled.button`
    ${iconButtonCss}
    width: 22px;
    min-width: 22px;
    height: 22px;
    min-height: 22px;
    font-size: 0.9rem;
    margin-left: 0.4rem;
    pointer-events: all;
    color: var(--pw-danger, #dc3545);

    &:hover:not(:disabled),
    &:focus:not(:disabled) {
        background: var(--pw-danger-bg, #fde8e8);
        border-color: var(--pw-danger, #dc3545);
        color: var(--pw-danger, #dc3545);
    }

    &:focus {
        outline: none;
    }
`;
